import { useState } from 'react';
import { doc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { LogOut, User as UserIcon } from 'lucide-react';
import { auth, db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useDocRow } from '@/lib/useDocs';
import { roleLabel } from '@/lib/format';
import { useLanguage } from '@/contexts/LanguageContext';

type UserRow = { name: string; phone: string; role: string };

/**
 * Who the app thinks you are, and the way out.
 *
 * Name, phone and role are all shown because none of them appear anywhere else
 * in the interface, and all three are what someone checks when a screen is
 * empty or a page refuses them: the usual cause is being signed in as the
 * wrong role, which is invisible until something says it out loud.
 *
 * There is no avatar image anywhere in this product — sign-in is a phone
 * number and nothing uploads a photo — so the avatar is the first letter of
 * the name, and the generic icon only when there is no name to take it from.
 */
export function UserMenu() {
  const { user, role } = useAuth();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const { row } = useDocRow<UserRow>(user ? doc(db, 'users', user.uid) : null, [user?.uid]);

  const name = (row?.name || '').trim();
  // Array.from, not [0]: an Arabic name is fine either way, but a name that
  // starts with an astral character would be sliced into half a code point.
  const initial = name ? Array.from(name)[0] : null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('userMenuOpen')}
        aria-expanded={open}
        className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-base font-semibold text-primary-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
        {initial ? initial : <UserIcon className="h-5 w-5" aria-hidden="true" />}
      </button>

      {open ? (
        <>
          {/* A click anywhere else closes it. Without this the menu survives
              navigation and hangs over the next page. */}
          <div
            data-testid="user-menu-backdrop"
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          {/* The avatar sits at the reading-end of the header, so the panel
              opens inward from that corner in either direction. */}
          <div className="absolute z-50 mt-2 w-64 rounded-lg border border-border-subtle bg-white py-2 shadow-lg rtl:left-0 ltr:right-0">
            <div className="flex flex-col gap-1 border-b border-border-subtle px-4 py-3">
              <span className="truncate font-semibold text-ink">{name || t('userNoName')}</span>
              <span className="text-sm text-muted" dir="ltr">{row?.phone || ''}</span>
              <span className="text-xs text-muted">{roleLabel(role || 'guest')}</span>
            </div>
            <button
              type="button"
              onClick={() => { setOpen(false); void signOut(auth); }}
              className="flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-sm text-ink hover:bg-surface"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              {t('signOut')}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
